<template>
  <b-card
    no-body
    class="timesheets-inputs"
    body-class="pt-0"
    header-class="bg-transparent border-bottom-0"
    footer-class="bg-transparent border-top-0"
  >

    <template #header>
      <div v-if="isMobileView">
        <div class="list-group-item border-0 d-flex justify-content-between align-items-center">
          <div class="flex-grow-1 pl-2">
            <h3 class="text-uppercase text-primary font-weight-bold mb-0">
              {{getWeekday()}}
              <i v-if="dayData.Remarks" :class="[$getConst.icons.comment, 'ml-1 text-warning']"></i>
              <i v-if="hasOvertime(dayData)" :class="[$getConst.icons.exclamation_circle, 'ml-1 text-warning']"></i>
            </h3>
            <h4 class="text-primary mb-0">{{date | localDate}}</h4>
          </div>
          <span>
            <b-badge
              v-if="dayData.isHoliday === 'Yes'"
              pill
              variant="light"
            >
              <template v-if="parseFloat(dayData.std) > 0">
                {{ dayData.std | removeDecimalIfNotNecessary }}
                <span v-if="isHoursContract">hrs</span>
                <span v-else-if="isDaysContract">days</span>
              </template>
            </b-badge>
            <b-badge
              v-else-if="dayData.absence === '0'"
              pill
              variant="success"
            >
              {{ dayData.std | removeDecimalIfNotNecessary }}
              <span v-if="isHoursContract">hrs</span>
              <span v-else-if="isDaysContract">days</span>
            </b-badge>
            <template v-else>
              <b-badge pill variant="warning" class="d-flex">
                <span v-if="parseFloat(dayData.absenceQty) === 0.5" class="mr-1 font-size-sm"><sup>1</sup>&frasl;<sub>2</sub></span>{{ absenceType.Description }}
              </b-badge>
              <b-badge
                v-if="parseFloat(dayData.absenceQty) === 0.5"
                pill
                class="mt-2"
                variant="success"
              >
                {{ dayData.std | removeDecimalIfNotNecessary }}
                <span v-if="isHoursContract">hrs</span>
                <span v-else-if="isDaysContract">days</span>
              </b-badge>
            </template>
          </span>
        </div>
      </div>

      <div v-else>
        <h3 class="text-primary text-uppercase font-weight-bold text-center">
          {{getWeekday()}}
          <i v-if="dayData.Remarks" :class="[$getConst.icons.comment, 'ml-1 text-warning']"></i>
          <i v-if="hasOvertime(dayData)" :class="[$getConst.icons.exclamation_circle, 'ml-1 text-warning']"></i>
        </h3>
        <h4 class="text-primary text-uppercase font-weight-bold text-center">
          {{date | localDate}}
        </h4>
      </div>
    </template>

    <b-collapse :id="date" :visible="!isMobileView">
      <b-card-body id="timesheet-inputs" body-class="py-0">
        <multiselect
          v-if="isAbsenceManagement && !isHoliday"
          placeholder="Absence type"
          v-model="absenceType"
          :disabled="!isAbsenceManagement || isApprover() || timesheet.Status === $getConst.timesheetsStatus.submitted ||
          timesheet.Status === $getConst.timesheetsStatus.processed || timesheet.Status === $getConst.timesheetsStatus.approved"
          class="mb-3"
          :options="absenceTypeOptions"
          track-by="code"
          label="Description"
          select-label=""
          selected-label=""
          deselect-label='×'
          @input="updateControlAbsence"
        >
          <template slot="option" slot-scope="props">
            <div class="d-flex">
              <div>{{ props.option.Description }}</div>
              <div v-if="props.option.Control.toLowerCase() !== 'no'">
                <span v-if="parseInt(props.option.Remaining) < 0">: 0</span>
                <span v-else>: {{ props.option.Remaining }}</span>
              </div>
            </div>
          </template>
          <span slot="noResult">{{$t($getConst.pages.alerts.no_results_found)}}</span>
        </multiselect>

        <!-- Half day absence input -->
        <b-form-group v-if="isAbsenceSelected">
          <b-form-checkbox
            id="half-day-absence-checkbox"
            :checked="isHalfDayAbsence"
            :disabled="onlyHalfDayAbsence"
            name="check-button"
            switch
            @change="selectAbsenceQty"
          >
            Half day absence
          </b-form-checkbox>
        </b-form-group>

        <!--  Times Inputs  -->
        <div v-if="!isAbsenceSelected || (isHalfDayAbsence && isAbsenceSelected)">
          <!--  Normal hours  -->
          <TimesheetTimeInput
            :unit="unit"
            :disabled="isApprover() || (isDaysContract && isAbsenceManagement) || timesheet.Status === $getConst.timesheetsStatus.submitted ||
            timesheet.Status === $getConst.timesheetsStatus.processed || timesheet.Status === $getConst.timesheetsStatus.approved"
            v-model="std"
            :is-half-day="isHalfDayAbsence"
            :validation="$v.std"
            @input="update()"
          />

          <!--  Overtime 1  -->
          <TimesheetTimeInput
            v-if="isToDisplayOvertime(1)"
            :unit="unit"
            :disabled="isApprover() || timesheet.Status === $getConst.timesheetsStatus.submitted ||
            timesheet.Status === $getConst.timesheetsStatus.processed || timesheet.Status === $getConst.timesheetsStatus.approved"
            :label="overtimeTypes.ot1"
            v-model="ot1"
            @input="update()"
          />

          <!--  Overtime 2  -->
          <TimesheetTimeInput
            v-if="isToDisplayOvertime(2)"
            :unit="unit"
            :disabled="isApprover() || timesheet.Status === $getConst.timesheetsStatus.submitted ||
            timesheet.Status === $getConst.timesheetsStatus.processed || timesheet.Status === $getConst.timesheetsStatus.approved"
            :label="overtimeTypes.ot2"
            v-model="ot2"
            @input="update()"
          />

          <!--  Overtime 3  -->
          <TimesheetTimeInput
            v-if="isToDisplayOvertime(3)"
            :unit="unit"
            :disabled="isApprover() || timesheet.Status  === $getConst.timesheetsStatus.submitted ||
            timesheet.Status === $getConst.timesheetsStatus.processed || timesheet.Status === $getConst.timesheetsStatus.approved"
            :label="overtimeTypes.ot3"
            v-model="ot3"
            @input="update()"
          />
        </div>

        <!--  Comment  -->
        <b-form-group id="comment" :label="$t($getConst.pages.timesheet.comment)" label-class="text-primary" label-for="comment-input">
          <b-form-textarea
            id="comment-input"
            :disabled="isApprover() || timesheet.Status === $getConst.timesheetsStatus.submitted ||
            timesheet.Status === $getConst.timesheetsStatus.processed || timesheet.Status === $getConst.timesheetsStatus.approved"
            v-model.trim="$v.comment.$model"
            size="lg"
            :class="{ 'is-invalid': $v.comment.$invalid}"
          ></b-form-textarea>
          <AppFormFeedback :errors="$v.comment"/>
        </b-form-group>
      </b-card-body>
    </b-collapse>

    <h4 v-if="isMobileView" v-b-toggle="date" class="text-center my-auto border-top py-1 mx-3">
      <b-link class="text-secondary"><i :class="[$getConst.icons.chevron_down, 'collapse-toggle']"></i></b-link>
    </h4>
    <!--  Buttons  -->
    <template #footer v-if="!isMobileView && timesheet.Frequency.toLowerCase() === 'monthly'">
      <AppConfirmButtons
        :is-disabled="$v.comment.$invalid"
        submit-text="summary"
        no-cancel
        @onSubmit="closeDayForm()"
      />
    </template>

  </b-card>
</template>

<script>
  import TimesheetTimeInput from "./TimesheetTimeInput";
  import AppConfirmButtons from "../../../components/common/utils/AppConfirmButtons";
  import {formatDate} from "../../../shared/services/date";
  import {required, minValue, maxValue, maxLength} from 'vuelidate/lib/validators'
  import AppFormFeedback from "../../../components/common/form/AppFormFeedback";
  import {getManagedAbsences} from "../../../shared/services/timesheets";

  export default {
    name: "TimesheetForm",
    components: {AppFormFeedback, AppConfirmButtons, TimesheetTimeInput},
    model: {
      prop: 'timesheet',
      event: 'updateDay'
    },
    props: {
      timesheet: null,
      date: null,
      dayIndex: null,
      isMobileView: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        dayData: null,
        unit: null,
        overtimeTypes: [],
        absenceTypeOptions: [],
        absenceType: null,
        selectedAbsence: null,
        std: null,
        ot1: null,
        ot2: null,
        ot3: null,
        comment: null,
        absenceQty: null,
        onlyHalfDayAbsence: false
      }
    },
    computed: {
      isHalfDayAbsence() {
        return parseFloat(this.absenceQty) === 0.5;
      },
      isAbsenceSelected() {
        return this.absenceType !== null;
      },
      isHoliday() {
        return this.timesheet.Days.Days[this.dayIndex].isHoliday.toLowerCase() === "yes";
      },
      isAbsenceManagement() {
        return !!(this.timesheet.Days.Absences && this.timesheet.Days.Absences[0].Managed === "Yes");
      },
      isDaysContract() {
        return this.unit.toLowerCase() === 'day';
      },
      isHoursContract() {
        return this.unit.toLowerCase() === 'hour';
      }
    },
    validations() {
      if (
        (
          this.ot1 !== "0.00" ||
          this.ot2 !== "0.00" ||
          this.ot3 !== "0.00"
        )
      ) {
        return {
          comment: {required, maxLength: maxLength(100)},
          std: {minValue: minValue(0), maxValue: this.isHoursContract ? maxValue(24) :maxValue(1)},
          ot1: {minValue: minValue(0), maxValue: this.isHoursContract ? maxValue(24) :maxValue(1)},
          ot2: {minValue: minValue(0), maxValue: this.isHoursContract ? maxValue(24) :maxValue(1)},
          ot3: {minValue: minValue(0), maxValue: this.isHoursContract ? maxValue(24) :maxValue(1)}
        }
      } else {
        return {
          comment: {maxLength: maxLength(100)},
          std: {minValue: minValue(0), maxValue: this.isHoursContract ? maxValue(24) :maxValue(1)}
        }
      }
    },
    methods: {
      getSelectedAbsenceIndex(code) {
        return this.timesheet.Days.Absences[0].AbsencesList.findIndex(item => item.code === code);
      },
      selectAbsenceQty(isHalfDayAbsence) {
        let index = this.getSelectedAbsenceIndex(this.selectedAbsence.code);
        if (isHalfDayAbsence) {
          this.absenceQty = "0.50";
          this.timesheet.Days.Absences[0].AbsencesList[index].Remaining += 0.5;
          if (this.isDaysContract) {
            this.std = "0.50";
          } else {
            this.std = "4.00"
          }
        } else {
          this.timesheet.Days.Absences[0].AbsencesList[index].Remaining -= this.absenceQty;
          this.absenceQty = "1.00";
          this.std = "0.00";
        }
        this.update();
      },
      hasOvertime(dayData) {
        return parseFloat(dayData.ot1) > 0 || parseFloat(dayData.ot2) > 0 || parseFloat(dayData.ot3) > 0;
      },
      updateControlAbsence(selectedAbsence){
        this.onlyHalfDayAbsence = false;
        // if an absence was selected
        if (selectedAbsence !== null) {

          // if an another absence was selected
          if (this.selectedAbsence) {
            let odlAbsenceIndex = this.getSelectedAbsenceIndex(this.selectedAbsence.code);
            if (this.timesheet.Days.Absences[0].AbsencesList[odlAbsenceIndex].Control.toLowerCase() === "yes") {
              this.timesheet.Days.Absences[0].AbsencesList[odlAbsenceIndex].Remaining += parseFloat(this.absenceQty);
            }
          }

          this.absenceQty = "1.00";
          let index = this.getSelectedAbsenceIndex(selectedAbsence.code);

          // if selected absence has control
          if (this.timesheet.Days.Absences[0].AbsencesList[index].Control.toLowerCase() === "yes") {
            // if there's no remaining sold
            if(parseFloat(this.timesheet.Days.Absences[0].AbsencesList[index].Remaining) <= 0){
              this.absenceType = null;
              this.absenceQty = "0.00";
              if (this.unit.toLowerCase() === 'day') {
                this.std = "1.00";
              } else {
                this.std = "8.00"
              }
              this.$snotify.error(
                this.$t(this.$getConst.pages.alerts.absence_not_remaining),
                selectedAbsence.Description
              );
            }
            // else if only half day remaining
            else if (parseFloat(this.timesheet.Days.Absences[0].AbsencesList[index].Remaining) === 0.5) {
              this.onlyHalfDayAbsence = true;
              this.absenceQty = 0.5;
              this.timesheet.Days.Absences[0].AbsencesList[index].Remaining -= 0.5;
              if (this.isDaysContract) {
                this.std = "0.50";
              } else {
                this.std = "4.00"
              }
            }
            // else if there's remaining sold
            else {
              this.timesheet.Days.Absences[0].AbsencesList[index].Remaining--;
              this.makeHoursToZero();
            }
          }
          // else if selected absence has no control
          else {
            this.makeHoursToZero();
          }
        }
        // else if an absence was deselected (working day selected)
        else {
          let index = this.getSelectedAbsenceIndex(this.selectedAbsence.code);
          if (this.timesheet.Days.Absences[0].AbsencesList[index].Control.toLowerCase() === "yes") {
            let rem = parseFloat(this.timesheet.Days.Absences[0].AbsencesList[index].Remaining);
            this.timesheet.Days.Absences[0].AbsencesList[index].Remaining = rem + parseFloat(this.absenceQty);
          }
          this.absenceQty = "0.00";
          if (this.timesheet.Unit.toLowerCase() === 'hour') {
            this.std = "8.00";
          } else {
            this.std = "1.00";
          }
        }
        this.selectedAbsence = this.absenceType;
        this.update();
      },
      initForm() {
        this.fillAbsenceDropdown();
        this.unit = this.timesheet.Unit;
        this.dayData = this.timesheet.Days.Days[this.dayIndex];
        this.absenceQty = this.timesheet.Days.Days[this.dayIndex].absenceQty;
        this.std = this.timesheet.Days.Days[this.dayIndex].std;
        this.ot1 = this.timesheet.Days.Days[this.dayIndex].ot1;
        this.ot2 = this.timesheet.Days.Days[this.dayIndex].ot2;
        this.ot3 = this.timesheet.Days.Days[this.dayIndex].ot3;
        this.comment = this.timesheet.Days.Days[this.dayIndex].Remarks;
        if (this.timesheet.Days.Days[this.dayIndex].absence === "1") {
          this.absenceType = this.absenceTypeOptions.find(
            element => element.code === this.timesheet.Days.Days[this.dayIndex].absenceCode
          );
          this.selectedAbsence = Object.assign({}, this.absenceType);

          if (parseFloat(this.absenceType.Remaining) === 0 && parseFloat(this.absenceQty) === 0.5) {
            this.onlyHalfDayAbsence = true;
          }
        } else {
          this.absenceType = null;
          this.selectedAbsence = null;
        }
      },
      getWeekday() {
        const dateObject = new Date(formatDate(this.date));
        return dateObject.toLocaleDateString(this.$i18n.locale, { weekday: 'long' });
      },
      fillAbsenceDropdown() {
        this.absenceTypeOptions = getManagedAbsences(this.timesheet);
      },
      isToDisplayOvertime(overtimeNb) {
        return this.overtimeTypes['ot' + overtimeNb].toLowerCase() !== "overtime"+overtimeNb ||
          this.timesheet.Days.Days[this.dayIndex]['ot' + overtimeNb] !== "0.00";
      },
      update() {
        let stdTotal = 0;
        this.timesheet.Days.Days.forEach(day => {
          stdTotal += +day.std;
        });
        this.timesheet.TotalUnits = stdTotal;
        this.timesheet.Days.Days[this.dayIndex].absenceQty = this.absenceQty;
        this.timesheet.Days.Days[this.dayIndex].std = this.std;
        this.timesheet.Days.Days[this.dayIndex].ot1 = this.ot1;
        this.timesheet.Days.Days[this.dayIndex].ot2 = this.ot2;
        this.timesheet.Days.Days[this.dayIndex].ot3 = this.ot3;
        this.timesheet.Days.Days[this.dayIndex].Remarks = this.comment;
        if (this.isAbsenceSelected) {
          this.timesheet.Days.Days[this.dayIndex].absence = "1";
          this.timesheet.Days.Days[this.dayIndex].absenceCode = this.absenceType.code;
        } else {
          this.timesheet.Days.Days[this.dayIndex].absence = "0";
          this.timesheet.Days.Days[this.dayIndex].absenceCode = "";
          this.timesheet.Days.Days[this.dayIndex].absenceQty = "0.00";
        }
        this.$emit("updateDay", this.timesheet);

      },
      closeDayForm() {
        this.$emit("closeDayForm");
      },
      makeHoursToZero() {
        this.std = "0.00";
        this.ot1 = "0.00";
        this.ot2 = "0.00";
        this.ot3 = "0.00";
      }
    },
    watch: {
      date() {
        this.initForm();
      }
    },
    created() {
      this.overtimeTypes = this.timesheet.Days.Types[0];
      this.initForm();
    },
    updated(){
      this.timesheet.Days.Days[this.dayIndex].Remarks = this.comment;
    }
  }
</script>

<style scoped>
  .not-collapsed .collapse-toggle{
    transform: rotate(180deg);
    transition: .2s;
  }
  .collapsed .collapse-toggle{
    transform: rotate(0deg);
    transition: .2s;
  }
</style>
